/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Organization } from "../../../models/interfaces/organization";
import { useAuthState } from "../../../utilities/contexts/auth-state-context";
import OrganizationService from "../../../utilities/services/organization-service";
import OrganizationUtil, { AvailableLimits } from "../../../utilities/organization-util";
import { useHistory } from "react-router";
import { FormikTextField } from "../../formik-fields/formik-text-field";
import { AppContentType } from "../../../models/enumerations/app-content-type";
import { AppContent } from "../../../models/interfaces/app-content";
import { PlanType } from "../../../models/enumerations/plan-type";
import { enqueueSnackbar } from "notistack";
import { Button } from "../../button/button";
import { useTranslation } from 'react-i18next';
import { createLogger } from "../../../utilities/logger";

const log = createLogger("EditSubOrganizationForm");

type EditSubOrganizationFormProps = {
    organizationId?: string,
    redirect?: string,
    onSave?: Function,
    onCancel?: Function,
    /** Hide the form's own heading (e.g. when the wrapping modal shows the title). */
    hideTitle?: boolean,
    /** Pre-loaded organization for edit mode; skips the fetch and prevents flashing create-mode values. */
    organization?: Organization,
    /** Pre-loaded parent (head) organization; used for display and (with availableLimits) skips the fetch. */
    parentOrganization?: Organization,
    /** Pre-computed remaining capacity the parent can still allocate; skips the computation. */
    availableLimits?: AvailableLimits,
};

interface formValues {
    organizationName: string,
    userLimit: number | undefined,
    mySymmioLicense: number | undefined,
}

const COMPONENT_CLASS = "c-organization-modal";

const copyAppContent = (source?: Organization['appContent']): Organization['appContent'] => {
    if (source) {
        return {
            [AppContentType.PAIN]: { ...source[AppContentType.PAIN] },
            [AppContentType.BEHAVIORAL_HEALTH]: { ...source[AppContentType.BEHAVIORAL_HEALTH] },
        };
    }
    return {
        [AppContentType.PAIN]: {} as AppContent,
        [AppContentType.BEHAVIORAL_HEALTH]: {} as AppContent,
    };
};

export const EditSubOrganizationForm: React.FC<EditSubOrganizationFormProps> = (props: EditSubOrganizationFormProps) => {
    const {
        organizationId,
        redirect,
        onSave,
        onCancel,
        hideTitle,
        organization: preloadedOrganization,
        parentOrganization: preloadedParent,
        availableLimits: preloadedLimits,
    } = props;
    const { state, setState } = useAuthState();
    const history = useHistory();
    const { t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(true);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [organization, setOrganization] = useState<Organization | undefined>(preloadedOrganization);
    const [parentOrganization, setParentOrganization] = useState<Organization | undefined>(preloadedParent);
    const [availableLimits, setAvailableLimits] = useState<AvailableLimits | undefined>(preloadedLimits);
    const [initialForm, setInitialForm] = useState<formValues>(
        preloadedOrganization
            ? {
                organizationName: preloadedOrganization.name ?? '',
                userLimit: preloadedOrganization.userLimit,
                mySymmioLicense: preloadedOrganization.mySymmioLicense,
            }
            : { organizationName: '', userLimit: undefined, mySymmioLicense: undefined }
    );
    const formikRef = useRef<any>(null);
    const [parentNameError, setParentNameError] = useState<string>();

    useEffect(() => {
        const loadData = async () => {
            if (!organizationId) {
                const parentOrg = preloadedParent
                    ?? (state.user?.organizationId ? await OrganizationService.get(state.user.organizationId) : undefined);

                if (parentOrg) {
                    setParentOrganization(parentOrg);
                    setAvailableLimits(preloadedLimits ?? await OrganizationUtil.getAvailableLimits(parentOrg));
                } else {
                    setParentNameError(t('organization.edit.err_cantRetrieveParent'));
                }

                setLoading(false);
                return
            };

            const o = preloadedOrganization ?? await OrganizationService.get(organizationId);

            if (!o) {
                setLoading(false);
                return
            };

            setOrganization(o);

            setInitialForm({
                organizationName: o.name ?? '',
                userLimit: o.userLimit,
                mySymmioLicense: o.mySymmioLicense,
            });

            const parentOrgId = o.parentOrganizationId;

            if (parentOrgId) {
                const parentOrg = preloadedParent ?? await OrganizationService.get(parentOrgId);
                if (parentOrg) {
                    setParentOrganization(parentOrg);
                    setAvailableLimits(preloadedLimits ?? await OrganizationUtil.getAvailableLimits(parentOrg));
                } else {
                    setParentNameError(t('organization.edit.err_cantRetrieveParent'));
                }
            }

            setLoading(false);
        };

        loadData();

    }, [organizationId])

    const saveOrganization = async (values: formValues) => {
        setSubmitting(true);
        try {
            if (await OrganizationService.nameExists(values.organizationName, organization?.id)) {
                formikRef.current?.setErrors({ organizationName: t('pricing.organizationPage.err_alreadyExists') });
                setSubmitting(false);
                return;
            }

            const setAllocationErrors = () => {
                formikRef.current?.setErrors({
                    userLimit: t('organization.edit.err_allocationExceedsHead'),
                    mySymmioLicense: t('organization.edit.err_allocationExceedsHead'),
                });
            };

            if (!organization) {
                const parentOrgId = state.user?.organizationId;

                if (!parentOrgId) {
                    log.warn("Sub-organization creation blocked: no parent organization id", {
                        userId: state.user?.id,
                    });
                    setSubmitting(false);
                    return;
                }

                const parentOrg = await OrganizationService.get(parentOrgId);

                if (!parentOrg) {
                    log.warn("Sub-organization creation blocked: parent organization not found", {
                        organizationId: parentOrgId,
                    });
                    setSubmitting(false);
                    return;
                }

                // Two-level enforcement: a sub-organization cannot be a parent
                if (OrganizationUtil.isSubOrganization(parentOrg)) {
                    formikRef.current?.setErrors({ organizationName: t('organization.edit.err_subOrgOfSubOrg') });
                    log.warn("Sub-organization creation blocked: parent is a sub-organization", {
                        parentOrganizationId: parentOrg.id,
                        parentOrganizationName: parentOrg.name,
                    });
                    setSubmitting(false);
                    return;
                }

                // Plan gate: only enterprise / enterprise trial plans can have sub-organizations
                const plan = parentOrg.plan?.value;
                if (plan !== PlanType.Enterprise && plan !== PlanType.EnterpriseTrial) {
                    formikRef.current?.setErrors({ organizationName: t('organization.edit.err_planNotSupported') });
                    log.warn("Sub-organization creation blocked: plan does not support sub-organizations", {
                        parentOrganizationId: parentOrg.id,
                        plan,
                    });
                    setSubmitting(false);
                    return;
                }

                // Count gate: organizationLimit is the number of
                // sub-organizations the head may hold; the head itself is not
                // counted toward the limit.
                if (OrganizationUtil.isSubOrganizationLimitReached(parentOrg)) {
                    formikRef.current?.setErrors({ organizationName: t('organization.edit.err_subOrgCountLimit') });
                    log.warn("Sub-organization creation blocked: organization limit reached", {
                        parentOrganizationId: parentOrg.id,
                        subOrganizationCount: OrganizationUtil.getSubOrganizationCount(parentOrg),
                        organizationLimit: Number(parentOrg.organizationLimit ?? 1),
                    });
                    setSubmitting(false);
                    return;
                }

                // Allocation validation against the head's remaining capacity
                const available = await OrganizationUtil.getAvailableLimits(parentOrg);
                if (Number(values.userLimit ?? 0) > available.userLimit || Number(values.mySymmioLicense ?? 0) > available.licenseLimit) {
                    setAllocationErrors();
                    log.warn("Sub-organization creation blocked: allocation exceeds parent capacity", {
                        parentOrganizationId: parentOrg.id,
                        requestedUserLimit: Number(values.userLimit ?? 0),
                        requestedLicenseLimit: Number(values.mySymmioLicense ?? 0),
                        availableUserLimit: available.userLimit,
                        availableLicenseLimit: available.licenseLimit,
                    });
                    setSubmitting(false);
                    return;
                }

                const o: Organization = {
                    name: values.organizationName.trim(),
                    status: parentOrg.status,
                    parentOrganizationId: parentOrg.id,
                    userLimit: Number(values.userLimit),
                    mySymmioLicense: Number(values.mySymmioLicense),
                    userCount: 0,
                    mySymmioLicenseCount: 0,
                    appContent: copyAppContent(parentOrg.appContent),
                }

                if (parentOrg.testingOrg === true) {
                    o.testingOrg = true;
                }

                const savedOrg = await OrganizationService.save(o, state.user);

                if (!savedOrg || !savedOrg.id) {
                    log.error("Sub-organization save returned no record", {
                        organizationId: parentOrgId,
                    });
                    return;
                }

                // carve the allocated slice out of the head's remaining capacity
                const updatedParent: Organization = { ...parentOrg };
                if (values.userLimit !== undefined) {
                    updatedParent.userLimit = Number(parentOrg.userLimit ?? 0) - Number(values.userLimit);
                }
                if (values.mySymmioLicense !== undefined) {
                    updatedParent.mySymmioLicense = Number(parentOrg.mySymmioLicense ?? 0) - Number(values.mySymmioLicense);
                }
                await OrganizationService.save(updatedParent, state.user);

                // rebuild the head's subOrganizationIds array from the child FK
                // (updateSubOrganizations is the single writer of that cache)
                const refreshedParent = await OrganizationUtil.updateSubOrganizations(parentOrg.id, state.user);

                // refresh the org in auth state so the org switcher reflects the new sub-org
                if (refreshedParent && state.organization?.id === parentOrg.id) {
                    setState((s) => ({
                        ...s, ...{ organization: refreshedParent }
                    }));
                }
            } else {
                const o: Organization = { ...organization };

                o.name = values.organizationName.trim();
                o.userLimit = Number(values.userLimit ?? 0);
                o.mySymmioLicense = Number(values.mySymmioLicense ?? 0);

                const userDelta = (o.userLimit ?? 0) - Number(organization.userLimit ?? 0);
                const licenseDelta = (o.mySymmioLicense ?? 0) - Number(organization.mySymmioLicense ?? 0);

                if (o.parentOrganizationId) {
                    const parentOrg = await OrganizationService.get(o.parentOrganizationId);

                    if (parentOrg) {
                        const available = await OrganizationUtil.getAvailableLimits(parentOrg);
                        if (userDelta > 0 && userDelta > available.userLimit) {
                            formikRef.current?.setErrors({ userLimit: t('organization.edit.err_allocationExceedsHead') });
                            log.warn("Sub-organization update blocked: allocation exceeds parent capacity", {
                                parentOrganizationId: parentOrg.id,
                                organizationId: organization?.id,
                                requestedUserDelta: userDelta,
                                availableUserLimit: available.userLimit,
                            });
                            setSubmitting(false);
                            return;
                        }
                        if (licenseDelta > 0 && licenseDelta > available.licenseLimit) {
                            formikRef.current?.setErrors({ mySymmioLicense: t('organization.edit.err_allocationExceedsHead') });
                            log.warn("Sub-organization update blocked: allocation exceeds parent capacity", {
                                parentOrganizationId: parentOrg.id,
                                organizationId: organization?.id,
                                requestedLicenseDelta: licenseDelta,
                                availableLicenseLimit: available.licenseLimit,
                            });
                            setSubmitting(false);
                            return;
                        }
                    }

                    await OrganizationService.save(o, state.user);

                    if (parentOrg) {
                        // adjust the head's remaining capacity by the allocation delta
                        const updatedParent: Organization = { ...parentOrg };
                        if (userDelta !== 0) {
                            updatedParent.userLimit = Number(parentOrg.userLimit ?? 0) - userDelta;
                        }
                        if (licenseDelta !== 0) {
                            updatedParent.mySymmioLicense = Number(parentOrg.mySymmioLicense ?? 0) - licenseDelta;
                        }
                        await OrganizationService.save(updatedParent, state.user);

                        if (state.organization?.id === parentOrg.id) {
                            setState((s) => ({
                                ...s, ...{ organization: updatedParent }
                            }));
                        }
                    }
                } else {
                    await OrganizationService.save(o, state.user);
                }
            }

            log.info(organizationId ? "Sub-organization updated" : "Sub-organization created", {
                organizationId,
                name: values.organizationName.trim(),
            });

            setSubmitting(false);

            redirect && history.push(redirect);

            onSave && onSave();

            enqueueSnackbar(organizationId ? t('organization.edit.orgEdited') : t('organization.edit.orgCreated'), { variant: "toast", width: "450px" });
        } catch (e) {
            log.error(organizationId ? "Failed to update sub-organization" : "Failed to create sub-organization", {
                exception: e,
                organizationId,
            });
            setSubmitting(false);
        }
    }

    const maxUserLimit = (availableLimits?.userLimit ?? 0) + Number(organization?.userLimit ?? 0);
    const maxLicense = (availableLimits?.licenseLimit ?? 0) + Number(organization?.mySymmioLicense ?? 0);

    return (
        <Formik
            innerRef={formikRef}
            initialValues={initialForm}
            enableReinitialize
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={
                loading
                    ? null
                    : Yup.object().shape({
                        organizationName: Yup
                            .string()
                            .required(t('organization.edit.validation.orgName')),
                        userLimit: Yup
                            .number()
                            .typeError(t('organization.edit.validation.numeric'))
                            .transform((value) => (value === '' ? undefined : value))
                            .required(t('organization.edit.validation.userLimit'))
                            .min(1, t('organization.edit.validation.minValue', { min: 1 }))
                            .max(maxUserLimit, t('organization.edit.err_allocationExceedsHead')),
                        mySymmioLicense: Yup
                            .number()
                            .typeError(t('organization.edit.validation.numeric'))
                            .transform((value) => (value === '' ? undefined : value))
                            .required(t('organization.edit.validation.symmioLicense'))
                            .min(0, t('organization.edit.validation.minValue', { min: 0 }))
                            .max(maxLicense, t('organization.edit.err_allocationExceedsHead')),
                    })
            }
            onSubmit={(values: formValues) => {
                saveOrganization(values);
            }}
        >
            {({
                handleSubmit,
                isSubmitting,
            }) => (
                <form onSubmit={handleSubmit} noValidate style={{ width: '450px' }}>
                    {!hideTitle &&
                        <h1 style={{ marginTop: '56px', marginBottom: '8px' }}>{organization ? t('organization.edit.subOrgTitle') : t('organization.edit.addSubOrganization')}</h1>
                    }
                    <div>
                        <p style={{ fontSize: '12px', fontWeight: 700, marginBottom: '2px', opacity: '60%' }}>{t('organization.edit.fields.parentOrganization')}</p>
                        <p style={{ fontSize: '14px', marginBottom: '32px', opacity: '60%' }}>{parentOrganization?.name ?? parentNameError}</p>
                        <div className={`${COMPONENT_CLASS}__field`}>
                            <FormikTextField
                                name="organizationName"
                                label={t('organization.edit.fields.orgName')}
                                disabled={loading}
                                validateOnChange={false}
                                required
                            />
                        </div>
                        <div className={`${COMPONENT_CLASS}__field`}>
                            <FormikTextField
                                name="userLimit"
                                label={t('organization.edit.fields.userLimit')}
                                labelEndText={availableLimits ? t('organization.edit.availableCount', { count: availableLimits.userLimit }) : undefined}
                                disabled={loading}
                                validateOnChange={false}
                                required
                            />
                        </div>
                        <div className={`${COMPONENT_CLASS}__field`}>
                            <FormikTextField
                                name="mySymmioLicense"
                                label={t('organization.edit.fields.wellnessLimit')}
                                labelEndText={availableLimits ? t('organization.edit.availableCount', { count: availableLimits.licenseLimit }) : undefined}
                                disabled={loading}
                                validateOnChange={false}
                                required
                            />
                        </div>
                        <div style={{ margin: '40px 0', display: 'flex', justifyContent: organization || onCancel ? 'center' : 'left' }}>
                            {
                                onCancel &&
                                <Button
                                    type='link'
                                    buttonType="button"
                                    onClick={() => onCancel && onCancel()}
                                    buttonText={t('buttons.btn_cancel')}
                                    inputStyle={{
                                        padding: '5px 15px',
                                        margin: '5px 25px',
                                    }} />
                            }
                            <Button
                                buttonType="submit"
                                inputClassName="action-button"
                                disabled={submitting || loading}
                                buttonText={organization ? t('buttons.btn_save') : t('organization.edit.addSubOrganization')}
                            />
                        </div>
                    </div>
                </form>
            )}
        </Formik>
    );
}